import {
  Caption,
  ControlledAutocomplete,
  FieldsetCard,
  SplitContainer,
  useFormContainerContext,
  useValidatedWatch,
} from '@if/mui'
import { EFinancingProgram, ELoanPurpose } from '@if/core/types'
import { Stack } from '@mui/material'
import { CalculatorEstimateResultDto, EProtectionPlanCoverage } from '@src/types/CalculatorEstimateDto'
import { CreditRatingOption, FinancingProgramConfigs } from '@src/types/Constants'
import { isVariableInterest } from '@src/data/worksheet-selectors'
import { Merchant } from '@src/types/Merchant'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BusinessFeeDisplay } from './components/BusinessFeeDisplay'
import { CreditRatingSelector } from './components/CreditRatingSelector'
import { LenderFeeDisplay } from './components/LenderFeeDisplay'
import { LoanDetailsForm } from './components/LoanDetailsForm'
import { PaymentEstimateCard } from './components/PaymentEstimateCard'
import { ProtectionPlanDisplay } from './components/ProtectionPlanDisplay'
import {
  useCalculatorTermAndPlanOptions,
  usePossibleTerms,
  useTermAndPlanAutoSelection,
} from './personalLoanCalculator-hooks'

interface CalculatorFormProps {
  merchant: Merchant | null
  effectiveMerchantId: string
  isBanner: boolean
  subsidiaries: Merchant[] | null
  selectedMerchant?: Merchant
  onSelectMerchant: (merchant?: Merchant) => void
  /** Built by the parent, which needs them to resolve the rate when submitting. */
  creditRatingOptions: CreditRatingOption[]
  calculatedRate?: CalculatorEstimateResultDto | null
  isFetching: boolean
  /** The program-allowed date the estimate priced on; the term lookup must use the same one. */
  firstPaymentOn?: string
}

/**
 * The calculator's fields (inside the FormContainer). Loads payment-plan/term
 * options live; the estimate is computed by the parent and passed in as props.
 */
export const CalculatorForm = ({
  merchant,
  effectiveMerchantId,
  isBanner,
  subsidiaries,
  selectedMerchant,
  onSelectMerchant,
  creditRatingOptions,
  calculatedRate,
  isFetching,
  firstPaymentOn,
}: CalculatorFormProps) => {
  const { t } = useTranslation()
  const { methods, setValidationContext } = useFormContainerContext<PersonalLoanCalculatorSchemaType>()
  const { control, setValue } = methods

  const inputsDisabled = isBanner && !selectedMerchant

  // Medicard categories (Medical, Dentistry, Aesthetics) get a higher request-amount ceiling.
  const isMedicardServiceCategory =
    merchant?.serviceCategory === ELoanPurpose.Medical ||
    merchant?.serviceCategory === ELoanPurpose.Dentistry ||
    merchant?.serviceCategory === ELoanPurpose.Aesthetics
  const personalConfig = FinancingProgramConfigs[EFinancingProgram.Personal]
  const maxLoanAmount = isMedicardServiceCategory ? personalConfig.maxLoanAmountMedicard : personalConfig.maxLoanAmount

  // Merchant (and its category) can resolve after mount for banner users — feed the
  // resolver via context so `amountRequested`'s max validates against the right ceiling.
  useEffect(() => {
    setValidationContext('maxLoanValue', maxLoanAmount)
  }, [maxLoanAmount, setValidationContext])

  // Live values drive the option lists (not the estimate); the amount is debounced below.
  const { paymentPlanId, term, creditRatingMinErs, protectionPlan } = useWatch({ control })

  const wantsProtection = (protectionPlan ?? EProtectionPlanCoverage.None) !== EProtectionPlanCoverage.None

  // A promotional plan's rate wins server-side, so the rating selection can't move the estimate.
  const promoPlanSetsRate = isVariableInterest(paymentPlanId ?? '')

  // Re-anchor to the best band only when the current selection no longer exists —
  // resetting on every `creditRatingOptions` change would stomp the user's choice.
  useEffect(() => {
    if (!creditRatingOptions.some((option) => option.minErs === creditRatingMinErs))
      setValue('creditRatingMinErs', creditRatingOptions[0].minErs)
  }, [creditRatingOptions, creditRatingMinErs, setValue])

  const { value: settledAmount, isValid: isAmountValid } = useValidatedWatch({ control, name: 'amountRequested' })
  const amountRequested = isAmountValid ? settledAmount : undefined
  const isAmountOutOfRange = !!settledAmount && !isAmountValid

  // Per-term legality of the protection plan; needs the rate the first estimate resolves.
  const possibleTerms = usePossibleTerms({
    amountRequested,
    paymentPlanId,
    creditRatingInterestRate: calculatedRate?.interestRate ?? 0,
    merchantId: effectiveMerchantId,
    provinceIso: merchant?.province,
    includeInsurance: wantsProtection,
    isJointCoverage: protectionPlan === EProtectionPlanCoverage.Joint,
    firstPaymentOn,
  })

  // Insurance only on terms whose rate-with-insurance stays legal; `undefined` = terms not loaded.
  const insuranceSupport = useMemo(() => {
    const matchingTerm = possibleTerms?.monthlyTerms?.find((item) => item.term === Number(term))
    return matchingTerm?.effectiveRateWithInsuranceIsLegal
  }, [possibleTerms, term])

  // Drop coverage once a term answers "not legal" (ignoring mid-flight refetches).
  useEffect(() => {
    if (insuranceSupport === false && wantsProtection) {
      setValue('protectionPlan', EProtectionPlanCoverage.None)
    }
  }, [insuranceSupport, wantsProtection, setValue])

  const termAndPlan = useCalculatorTermAndPlanOptions({
    merchantId: effectiveMerchantId,
    provinceIso: merchant?.province,
    amountRequested,
    creditRatingMinErs,
    protectionPlan,
    firstPaymentOn,
  })

  useTermAndPlanAutoSelection({ options: termAndPlan.options, paymentPlanId, term, setValue })

  return (
    <SplitContainer
      main={
        <Stack spacing={3}>
          {isBanner && (
            <FieldsetCard title={t('calculator.selectMerchant')}>
              <ControlledAutocomplete
                name="selectedMerchantId"
                control={control}
                label={t('calculator.selectMerchant')}
                options={(subsidiaries ?? []).map((m) => m.id)}
                getOptionLabel={(id) => subsidiaries?.find((m) => m.id === id)?.name ?? ''}
                getOptionDisabled={(id) => !subsidiaries?.find((m) => m.id === id)?.isActive}
                onChange={(id) => onSelectMerchant(subsidiaries?.find((m) => m.id === id))}
                disableClearable
                fullWidth
              />
            </FieldsetCard>
          )}

          <LoanDetailsForm
            disabled={inputsDisabled}
            termAndPlanOptions={termAndPlan.options}
            isLoadingTermAndPlan={termAndPlan.isLoading}
            maxLoanAmount={maxLoanAmount}
          />

          <CreditRatingSelector
            options={creditRatingOptions}
            disabled={inputsDisabled}
            promoPlanSetsRate={promoPlanSetsRate}
          />

          <ProtectionPlanDisplay supportsInsurance={insuranceSupport === true} />

          <BusinessFeeDisplay calculatedRate={calculatedRate} />

          <LenderFeeDisplay calculatedRate={calculatedRate} />

          <Caption>{t('calculator.disclaimer')}</Caption>
        </Stack>
      }
      aside={
        <PaymentEstimateCard
          calculatedRate={calculatedRate}
          isFetching={isFetching}
          isAmountOutOfRange={isAmountOutOfRange}
        />
      }
    />
  )
}
